<template>
  <a-modal
    :title="`Verify OTP - ${title}`"
    :open="showOtpModal"
    :confirm-loading="confirmingOtp"
    :maskClosable="false"
    okText="Verify"
    cancelText="Close"
    @ok="handleVerifyOtp"
    @cancel="onCloseModal"
    centered
  >
    <div class="mt-4 d-flex flex-column justify-content-center align-items-center" id="otpWrapper">
      <div class="otp-input-wrapper">
        <input
          v-for="(digit, index) in otpValue"
          :key="index"
          v-model="otpValue[index]"
          :class="{ is_filled: otpValue[index] }"
          @input="onInput($event, index)"
          @keydown.backspace="onBackspace($event, index)"
          @keydown.enter="handleEnterKey"
          @paste="handlePaste($event)"
          maxlength="1"
          type="text"
          class="otp-digit"
          ref="otpFields"
        />
      </div>
      <div v-if="authStore.receiveOtpOn === 'googleAuth'">
        <p>{{ googleAuthOtpMessage }}</p>
      </div>
      <div v-else-if="authStore.receiveOtpOn === 'sms'">
        <p>{{ smsOtpMessage }}</p>
      </div>
      <div v-else-if="disableResendButton && showRemainingTime">
        <p v-if="minutes > 0">Resend OTP in {{ minutes }} minutes {{ seconds }} seconds</p>
        <p v-else>Resend OTP in {{ seconds }} seconds</p>
      </div>
      <a-button
        v-if="!disableResendButton"
        type="primary"
        block
        class="my-4"
        size="large"
        :disabled="disableResendButton"
        @click="handleResendOtp"
      >
        Resend OTP
      </a-button>
    </div>
  </a-modal>
</template>

<script setup>
import { useAuthStore } from 'src/stores/auth'
import AuthService from 'src/services/auth'
import { onBeforeUnmount, onMounted, ref, toRefs, watch } from 'vue'
import { useToast } from 'vue-toastification'

const toast = useToast()
const authStore = useAuthStore()
const timeToSendOtp = 300
const googleAuthOtpMessage =
  'Please use the 6-digit OTP from your authenticator app for verification.'
const smsOtpMessage = 'Please use the 6-digit OTP sent on your mobile number for verification.'
const otpSentMessage =
  'We have just sent a 6-digit OTP to your email address. Please check your inbox and use the provided OTP for verification.'

const otpValue = ref(new Array(6).fill(''))
const otpFields = ref([])
const showRemainingTime = ref(false)
const disableResendButton = ref(true)
const confirmingOtp = ref(false)
const timerCount = ref(timeToSendOtp)
const timerInterval = ref(null)
const resendCount = ref(0)
const minutes = ref(null)
const seconds = ref(null)
const title = ref('Email')

const props = defineProps(['showOtpModal'])
const emit = defineEmits(['closeOtpModal'])
const { showOtpModal } = toRefs(props)

watch(showOtpModal, (value) => {
  if (value) {
    if (authStore.receiveOtpOn === 'googleAuth') {
      toast.success(googleAuthOtpMessage)
    } else if (authStore.receiveOtpOn === 'sms') {
      toast.success(smsOtpMessage)
    } else {
      toast.success(otpSentMessage)
    }
    otpValue.value = new Array(6).fill('')
    setTimeout(() => {
      otpFields.value[0]?.focus()
    }, 0)
  }
})

onMounted(async () => {
  setTimer()

  if(authStore.receiveOtpOn === 'googleAuth') {
    title.value = 'Authenticator App'
  }
  else if(authStore.receiveOtpOn === 'sms') {
    title.value = 'SMS'
  }
  else {
    title.value = 'Email'
  }

  if (authStore.receiveOtpOn == 'email' || authStore.receiveOtpOn === 'sms') {
    await AuthService.sendOTP()
  }
})

onBeforeUnmount(() => {
  if (timerInterval.value) clearInterval(timerInterval.value)
})

const onCloseModal = () => {
  emit('closeOtpModal', true)
  timerCount.value = timeToSendOtp
  if (timerInterval.value) clearInterval(timerInterval.value)
}

const setTimer = () => {

  showRemainingTime.value = true
  minutes.value = Math.floor(timerCount.value / 60)
  seconds.value = timerCount.value % 60

  timerInterval.value = setInterval(() => {
    if (timerCount.value > 0) {
      showRemainingTime.value = true
      timerCount.value--
      minutes.value = Math.floor(timerCount.value / 60)
      seconds.value = timerCount.value % 60
    } else {
      clearInterval(timerInterval.value)
      disableResendButton.value = false
      showRemainingTime.value = false
    }
  }, 1000)
}

const onInput = (event, index) => {
  const value = event.target.value
  if (value.length === 1 && index < otpValue.value.length - 1) {
    otpFields?.value[index + 1]?.focus()
  }
}

const onBackspace = (event, index) => {
  if (event.target.value === '' && index > 0) {
    otpFields?.value[index - 1]?.focus()
  }
}
const handlePaste = (event) => {
  const pastedData = event.clipboardData.getData('Text').slice(0, 6)
  if (pastedData.length === 6) {
    otpValue.value = pastedData.split('')
    setTimeout(() => {
      otpFields.value[5]?.focus()
    }, 0)
  }
  event.preventDefault()
}
const handleResendOtp = async () => {
  if (resendCount.value >= 2) {
    toast.error('Resend count exceeded, Please try sign in again!')
    return
  }
  resendCount.value++
  disableResendButton.value = true
  timerCount.value = timeToSendOtp
  otpValue.value = new Array(6).fill('')
  otpFields.value = []
  setTimer()
  await authStore.resendOTP()
}

const handleVerifyOtp = async () => {
  const otpInput = otpValue.value?.join('')
  if (otpInput?.length != 6) {
    toast.error('Please enter the 6-digit OTP')
    return
  }
  confirmingOtp.value = true
  await authStore.verifyOTP({ OTP: otpInput })
  confirmingOtp.value = false
}

const handleEnterKey = () => {
  const isAllFieldsFilled = otpValue.value.every((digit) => digit !== '')
  if (isAllFieldsFilled) {
    handleVerifyOtp()
  }
}
</script>

<style scoped>
.otp-input-wrapper {
  display: flex;
  gap: 20px;
  margin-bottom: 10px;
}

.otp-digit {
  width: 50px;
  padding: 10px;
  font-size: 18px;
  text-align: center;
  border: 1px solid rgba(0, 0, 0, 0.3);
  border-radius: 4px;
}

.otp-digit.is_filled {
  background-color: #e4e4e4;
}
</style>
